import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ApiId, TtdPriceModel} from '../../../../distribution-account/distribution-account.model';
import {TtdConfig, TtdGroup, TtdPartner, TtdRateType} from '../../../../target/target.service';
import {TtdDataRate} from '../../../../ttd/ttd-data-rate/ttd-data-rate.types';
import {map, startWith} from 'rxjs/operators';
import {FormControl} from '@angular/forms';
import {EMPTY, Observable} from 'rxjs';
import {ttdSystemPriceWarning, TtdSystemPriceWarning} from '../../../../ttd/ttd-utils';

@Component({
  selector: 'app-ttd-segment-price-dialog',
  template: `
    <h1 mat-dialog-title>TTD price configuration</h1>

    <mat-button-toggle-group style="margin: 10px" name="fontStyle" aria-label="Font Style"
                             [value]="mode"
                             (change)="changeState()">
      <mat-button-toggle *ngFor="let state of ['PARTNER', 'PARTNER_GROUP']" [value]="state"
                         [matTooltip]="'Sets price for '+state">{{ state | titlecase }}
      </mat-button-toggle>
    </mat-button-toggle-group>

    <app-acknowledge-overlay *ngIf="systemPriceWarning?.running"
                             [text]="systemPriceWarning?.text">
    </app-acknowledge-overlay>

    <div mat-dialog-content class="mat-body-1">
      <ng-container [ngSwitch]="mode">
        <mat-form-field data-cy="input-partner-select" *ngSwitchCase="'PARTNER'">
          <mat-label>Partners</mat-label>
          <input (ngModelChange)="handlePartnerSelect($event)"
                 [formControl]="partnerControl" matInput
                 appClearSpacesInAutocomplete
                 [autofocus]="!systemPriceWarning?.running"
                 placeholder="search"
                 name="partner"
                 [matAutocomplete]="partnerIdInput"/>
          <mat-autocomplete #partnerIdInput="matAutocomplete">
            <mat-option *ngFor="let option of filteredPartnerOptions | async"
                        [value]="option.partnerName"
                        data-cy="input-partner-select-option">
              {{ option.partnerName }} ({{ option.partnerId }})
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
        <mat-form-field data-cy="input-partner-group-select" *ngSwitchCase="'PARTNER_GROUP'">
          <mat-label>Partner Groups</mat-label>
          <input (ngModelChange)="handlePartnerGroupSelect($event)"
                 [formControl]="partnerGroupControl" matInput
                 appClearSpacesInAutocomplete
                 [autofocus]="!systemPriceWarning?.running"
                 placeholder="search"
                 name="partnerGroup"
                 [matAutocomplete]="partnerGroupNameInput"/>
          <mat-autocomplete #partnerGroupNameInput="matAutocomplete">
            <mat-option *ngFor="let option of filteredPartnerGroupOptions | async"
                        [value]="option.name"
                        data-cy="input-partner-group-select-option">
              {{ option.name }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
      </ng-container>

      <ng-container class="flex-container">
        <app-ttd-brand-selector [config]="data.ttdConfig"
                                [initial]="brandId"
                                [apiId]="data.accountApiId"
                                (out)="brandId = $event"></app-ttd-brand-selector>
        <div [ngClass]="{'disabled':isPriceDisabled()}">
          <app-ttd-data-rate (out)="onDataRateSubmit($event)" [initEditMode]="true" class="horizontal"
                             [rate]="dataRate">
          </app-ttd-data-rate>
        </div>
      </ng-container>
    </div>
  `,
  styleUrls: ['../account/account.component.scss', 'account-ttd.component.scss']
})
export class TtdSegmentPriceDialogComponent implements OnInit {

  partnerId = this.data.price?.buyerId;
  partnerGroupName = '';
  partnerGroupId: number | undefined;
  partnerIsEditable = true;

  partners = this.data.partners;
  partnerGroups = this.data.partnerGroups;

  partnerControl = new FormControl();
  partnerGroupControl = new FormControl();
  filteredPartnerOptions: Observable<TtdPartner[]> = EMPTY;
  filteredPartnerGroupOptions: Observable<TtdGroup[]> = EMPTY;

  dataRate!: TtdDataRate;

  systemPriceWarning!: TtdSystemPriceWarning;
  mode: 'PARTNER' | 'PARTNER_GROUP' = 'PARTNER';
  brandId?: string;

  constructor(
    private dialogRef: MatDialogRef<TtdSegmentPriceDialogComponent, TtdPriceModel>,
    @Inject(MAT_DIALOG_DATA) public data: TtdSegmentPriceDialogData) {
  }

  ngOnInit(): void {

    this.dataRate = {
      rateType: this.data.price?.type as TtdRateType,
      cpmAmountUsd: this.data.price?.cpm,
      percentOfMediaCostRate: this.data.price?.percentOfMediaCostRate
    };
    this.brandId = this.data.price?.brandId ?? this.data.ttdConfig.filter(it => it.apiId === this.data.accountApiId)[0].brandId;

    this.partnerIsEditable = !this.data.price;

    this.filteredPartnerOptions = this.partnerControl.valueChanges.pipe(
      startWith(''),
      map((value: string) => {
        const filterValue = value.toLowerCase();
        return this.partners.filter(option => option.partnerName.toLowerCase().includes(filterValue)
          || option.partnerId.toLowerCase().includes(filterValue));
      })
    );
    this.filteredPartnerGroupOptions = this.partnerGroupControl.valueChanges.pipe(
      startWith(''),
      map((value: string) => {
        const filterValue = value.toLowerCase();
        return this.partnerGroups.filter(option => option.name.toLowerCase().includes(filterValue));
      })
    );

    const optionalLastUpdateSystemRate = this.data.currentPrices
      .find((ttdPrice) => ttdPrice.buyerId === 'System')?.lastUpdated;
    this.systemPriceWarning = ttdSystemPriceWarning(optionalLastUpdateSystemRate);
  }

  onDataRateSubmit(rate: TtdDataRate) {
    this.confirm({
      buyerId: this.partnerId,
      groupId: this.partnerGroupId,
      type: rate.rateType,
      cpm: rate.cpmAmountUsd,
      percentOfMediaCostRate: rate.percentOfMediaCostRate,
      brandId: this.brandId,
    });
  }

  confirm(price: TtdPriceModel): void {
    this.dialogRef.close(price);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  handlePartnerSelect($eventInput: string) {
    const validInput = this.partners.find(x => x.partnerName === $eventInput);
    this.partnerId = validInput ? validInput.partnerId : '';
  }

  isPriceDisabled() {
    return !this.partnerId && !this.partnerGroupName;
  }

  changeState() {
    if (this.mode === 'PARTNER') {
      this.mode = 'PARTNER_GROUP';
      this.partnerId = '';
    } else {
      this.mode = 'PARTNER';
      this.partnerGroupName = '';
      this.partnerGroupId = undefined;
    }
  }

  handlePartnerGroupSelect($event: any) {
    const validInput = this.partnerGroups.find(x => x.name === $event);
    this.partnerGroupName = validInput ? validInput.name : '';
    this.partnerGroupId = validInput ? validInput.id : undefined;
  }
}

export type TtdSegmentPriceDialogData = {
  price?: TtdPriceModel,
  partners: TtdPartner[],
  partnerGroups: TtdGroup[],
  ttdConfig: TtdConfig[],
  accountApiId: ApiId,
  /**
   * Input all exising price rates. The dialog have to make decision based on existing system price.
   */
  currentPrices: TtdPriceModel[]
};
